'use client'

import { useMemo } from 'react'

import { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { usePdpSelectionDerived, type PdpSelectionDerived } from '@src/components/pl-base-widget/contexts/pdp-selection'
import ProductComparisonBrand from '@src/quince-site-common/src/domains/ProductComparisonBrand'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getBrandComparisonsForDevice } from '@src/quince-site-pdp/src/organisms/productPageSections/brandComparison/BrandComparison'

import {
  toBundleComparisonBrands,
  toProductComparisonBrands,
  type BundleBrandSelection,
} from './brand-comparison.adapter'

import type { BrandComparisonWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

export interface BrandComparisonModel {
  data: BrandComparisonWidgetData | undefined
  derived: PdpSelectionDerived | undefined
  brands: ProductComparisonBrand[]
  quincePrice: number
  giftCardInformation: {
    giftCardCredit: number | undefined
    productComparisionsBrands: ProductComparisonBrand[]
  }
}

/**
 * Shared data model for the BRAND_COMPARISON widget variants (DS + legacy).
 *
 * Builds the device-trimmed competitor `brands` from the widget's own data and reads the live Quince
 * price + gift-card/servings scalars the buy box (PRODUCT_PURCHASE) publishes via the page-widget store,
 * so the table reacts to variant selection. Returns `null` for the empty terminal state — buy box not
 * mounted yet (no resolved price) or no on-site competitor brand has a price for the current selection —
 * so both variants render nothing rather than an empty table.
 */
export function useBrandComparisonData(
  props: PLTypedWidgetProps<BrandComparisonWidgetData>,
): BrandComparisonModel | null {
  const data = props.data.brandComparisonData
  const derived = usePdpSelectionDerived()

  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType !== DEVICE_TYPES.DESKTOP

  const productId = data?.products ? Object.keys(data.products)[0] : undefined

  // Bundle only. Redux republishes a new `bundleSelections` array on every click, so key the memo on the
  // selections' VALUE (child id + its resolved cheapest variant) and rebuild a stable array from it.
  const bundleSelectionKey = derived?.bundleSelections
    ?.map(selection => `${selection.productId}:${selection.minPriceVariantId ?? ''}`)
    .join(',')

  const bundleSelections = useMemo<BundleBrandSelection[] | undefined>(() => {
    if (!bundleSelectionKey) return undefined
    return bundleSelectionKey.split(',').map(entry => {
      const [productId = '', variantId = ''] = entry.split(':')
      return { productId, minPriceVariantId: variantId ? Number(variantId) : undefined }
    })
  }, [bundleSelectionKey])

  const brands = useMemo(() => {
    if (!data) return []
    // Bundle first — otherwise the single-product path would compare one child against the bundle price.
    if (bundleSelections) {
      // No sort: non-PL `BrandComparisonBundle` renders the accumulation map in insertion order, i.e.
      // first-appearance order across children. Sorting here would reorder the columns vs non-PL.
      return getBrandComparisonsForDevice(toBundleComparisonBrands(data, bundleSelections), isMobile)
    }
    if (!productId) return []
    const allBrands = toProductComparisonBrands(data, productId, derived?.minPriceVariantId)
    // Sort ascending by resolved price AFTER the device slice (slicing first preserves which brands
    // are shown). The DS block sorts a defensive copy of `productComparisionsBrands` but renders the
    // gift-card rows from `giftCardInformation.productComparisionsBrands` as passed — pre-sorting the
    // one array both props share keeps every row's columns aligned. Legacy re-sorts in place with the
    // same comparator, so its render is unchanged.
    return getBrandComparisonsForDevice(allBrands, isMobile).sort(
      (a, b) => (a.prices[0]?.price ?? 0) - (b.prices[0]?.price ?? 0),
    )
  }, [data, productId, derived?.minPriceVariantId, isMobile, bundleSelections])

  const quincePrice = derived?.quincePrice

  if (brands.length === 0 || quincePrice === undefined) return null

  return {
    data,
    derived,
    brands,
    quincePrice,
    giftCardInformation: {
      giftCardCredit: derived?.storeCreditGrant,
      productComparisionsBrands: brands,
    },
  }
}
